import React from 'react';
import Link from 'next/link';
import BrandLogo from '@/components/ui/BrandLogo';

const footerLinks = {
  explore: [
    { label: 'Destinations', href: '/destinations' },
    { label: 'Tour Packages', href: '/packages' },
    { label: 'Hotels', href: '/hotels' },
    { label: 'Vehicle Rental', href: '/vehicles' },
    { label: 'Local Guides', href: '/guides' },
    { label: 'Search', href: '/search' },
  ],
  services: [
    { label: 'Discover Hidden Nepal', href: '/discover' },
    { label: 'Become a Guide', href: '/guides/become-guide' },
    { label: 'Provider Dashboard', href: '/provider' },
    { label: 'Community Leaderboard', href: '/discover/leaderboard' },
    { label: 'Travel Blog', href: '/blog' },
    { label: 'Trip Planner', href: '/trip-planner' },
  ],
  support: [
    { label: 'My Dashboard', href: '/dashboard' },
    { label: 'My Bookings', href: '/booking/my-bookings' },
    { label: 'Payment History', href: '/payments' },
    { label: 'Reviews', href: '/reviews' },
    { label: 'Chat & Support', href: '/chat' },
  ],
};

export default function Footer() {
  return (
    <footer style={{ background: 'linear-gradient(180deg, #1B3A6B 0%, #0F2347 100%)', borderTop: '1px solid rgba(26,188,212,0.1)' }} role="contentinfo">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-2">
            <div className="mb-5">
              <BrandLogo size={44} showTagline={true} asLink={true} textColor="light" />
            </div>
            <p className="text-sm leading-relaxed max-w-xs mb-6" style={{ color: 'rgba(245,240,232,0.5)' }}>
              Nepal&apos;s premium travel marketplace connecting tourists with verified local guides, tour packages, hotels, and authentic experiences.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'rgba(200,150,10,0.6)', flexShrink: 0 }} aria-hidden="true">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
                <span>support@yatranest.com</span>
              </div>
              <div className="flex items-center gap-2 text-sm" style={{ color: 'rgba(245,240,232,0.5)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'rgba(200,150,10,0.6)', flexShrink: 0 }} aria-hidden="true">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <span>24/7 Support Available</span>
              </div>
            </div>
          </div>

          {/* Explore */}
          <nav aria-label="Explore links">
            <h4 className="font-semibold text-sm mb-4" style={{ color: 'var(--himal-white)' }}>Explore</h4>
            <ul className="space-y-2.5">
              {footerLinks?.explore?.map((item) => (
                <li key={item?.href}>
                  <Link href={item?.href} className="text-sm transition-colors hover:text-gold" style={{ color: 'rgba(245,240,232,0.5)' }}>
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Services */}
          <nav aria-label="Services links">
            <h4 className="font-semibold text-sm mb-4" style={{ color: 'var(--himal-white)' }}>Services</h4>
            <ul className="space-y-2.5">
              {footerLinks?.services?.map((item) => (
                <li key={item?.href}>
                  <Link href={item?.href} className="text-sm transition-colors hover:text-gold" style={{ color: 'rgba(245,240,232,0.5)' }}>
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Support */}
          <nav aria-label="Support links">
            <h4 className="font-semibold text-sm mb-4" style={{ color: 'var(--himal-white)' }}>Support</h4>
            <ul className="space-y-2.5">
              {footerLinks?.support?.map((item) => (
                <li key={item?.href}>
                  <Link href={item?.href} className="text-sm transition-colors hover:text-gold" style={{ color: 'rgba(245,240,232,0.5)' }}>
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-5 p-3 rounded-xl" style={{ background: 'rgba(200,150,10,0.1)', border: '1px solid rgba(200,150,10,0.2)' }}>
              <div className="text-xs font-semibold mb-1" style={{ color: 'var(--gold-bright)' }}>AI Trip Planner</div>
              <Link href="/trip-planner" className="text-xs transition-colors hover:text-gold" style={{ color: 'rgba(245,240,232,0.6)' }}>
                Plan your Nepal trip with AI →
              </Link>
            </div>
          </nav>
        </div>
      </div>
      {/* Bottom bar */}
      <div style={{ borderTop: '1px solid rgba(26,188,212,0.08)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 sm:pt-5 flex flex-col sm:flex-row items-center justify-between gap-3" style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}>
          <span className="text-xs text-center sm:text-left" style={{ color: 'rgba(245,240,232,0.3)' }}>© 2026 YatraNest Nepal Pvt. Ltd. All rights reserved.</span>
          <div className="flex items-center gap-4 sm:gap-5 flex-wrap justify-center">
            <Link href="/blog" className="text-xs transition-colors hover:text-white/70" style={{ color: 'rgba(245,240,232,0.4)' }}>Blog</Link>
            <Link href="/search" className="text-xs transition-colors hover:text-white/70" style={{ color: 'rgba(245,240,232,0.4)' }}>Search</Link>
            <Link href="/search?q=privacy+policy" className="text-xs transition-colors hover:text-white/70" style={{ color: 'rgba(245,240,232,0.4)' }}>Privacy Policy</Link>
            <Link href="/search?q=terms+of+service" className="text-xs transition-colors hover:text-white/70" style={{ color: 'rgba(245,240,232,0.4)' }}>Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}